import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Loader2,
  Edit,
  Archive,
  Ban,
  User,
  MapPin,
  Phone,
  Mail,
  Building2,
  PoundSterling,
  Calendar,
  ClipboardList,
  AlertTriangle,
  ExternalLink,
  CheckCircle2,
  Clock,
  MessageSquarePlus,
  Trash2,
  StickyNote,
  Send,
  Package,
  UserCog,
  FolderOpen,
  ChevronDown,
  ChevronUp,
  Save,
  X,
  Plus,
  Briefcase,
  MessageSquare
} from 'lucide-react';
import { format, parseISO, differenceInDays } from 'date-fns';
import { FileBrowser } from '../components/FileBrowser';
import { CaptureFAB } from '../components/capture/CaptureFAB';

// Import extracted section components
import {
  CostBreakdownSection,
  ProgrammeDatesSection,
  HoursPerTradeSection,
  FeedbackSurveySection,
  STATUS_COLORS,
  formatCurrency,
  TRADE_OPTIONS,
  FEEDBACK_STATUS_OPTIONS,
  LOSS_REASON_OPTIONS
} from '../components/tenders/detail';

const STATUS_OPTIONS = ['Active', 'Submitted', 'Won', 'Lost', 'Archived', 'Void'];
const ENQUIRY_STATUS_OPTIONS = ['Pending', 'Received', 'No Response'];
const PLANNING_STATUS_OPTIONS = ['Approved', 'In Process', 'Exploring Options'];
const PROJECT_TYPE_OPTIONS = ['Extension', 'Loft Conversion', 'Garden Room', 'Garage Conversion', 'Refurb', 'New Build', 'Insurance', 'Other'];
const TENDER_TYPE_OPTIONS = [
  { value: 'DEV', label: 'Development' },
  { value: 'CON', label: 'Construction' }
];
const BUDGET_RANGE_OPTIONS = ['£10k - £20k', '£20k - £50k', '£50k - £80k', '£80k - £150k', '£150k+'];
const START_DATE_OPTIONS = ['1-2 months', '2-6 months', '6-12 months'];

// Note: TRADE_OPTIONS, FEEDBACK_STATUS_OPTIONS, STATUS_COLORS, formatCurrency, LOSS_REASON_OPTIONS 
// are imported from components/tenders/detail

export function TenderDetail() {
  const { tenderId } = useParams();
  const [tender, setTender] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [siteAddressSameAsClient, setSiteAddressSameAsClient] = useState(true);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [enquiryDialogOpen, setEnquiryDialogOpen] = useState(false);
  const [editEnquiryDialogOpen, setEditEnquiryDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [editData, setEditData] = useState({});
  const [newNoteContent, setNewNoteContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [adminUsers, setAdminUsers] = useState([]);
  const [structuralEngineers, setStructuralEngineers] = useState([]);
  const [architects, setArchitects] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [selectedEmailEnquiry, setSelectedEmailEnquiry] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [newEnquiry, setNewEnquiry] = useState({
    supplier_id: '',
    supplier_name: '',
    supplier_email: '',
    trade: '',
    description: '',
    date_sent: format(new Date(), 'yyyy-MM-dd'),
    status: 'Pending'
  });
  const [editingEnquiry, setEditingEnquiry] = useState(null);
  
  // State for new tender detail features
  const [costEditMode, setCostEditMode] = useState(false);
  const [costEditData, setCostEditData] = useState({});
  const [programmeExpanded, setProgrammeExpanded] = useState(false);
  const [programmeEditMode, setProgrammeEditMode] = useState(false);
  const [programmeData, setProgrammeData] = useState({});
  const [hoursExpanded, setHoursExpanded] = useState(false);
  const [hoursEditMode, setHoursEditMode] = useState(false);
  const [hoursData, setHoursData] = useState([]);
  const [feedbackData, setFeedbackData] = useState({});
  
  // State for inline Loss Reason editing
  const [lossReasonEditMode, setLossReasonEditMode] = useState(false);
  const [lossReasonData, setLossReasonData] = useState({ loss_reason: '', loss_reason_notes: '' });
  
  // State for status change dialogs (Items 4, 5, 6)
  const [feedbackSurveyDialogOpen, setFeedbackSurveyDialogOpen] = useState(false);
  const [contractDialogOpen, setContractDialogOpen] = useState(false);
  const [generateTenderDialogOpen, setGenerateTenderDialogOpen] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState(null); // Stores edit data for post-dialog processing

  const { api, isAdmin, isSurveyorOnly, canEditRecords, canDeleteRecords, canManageTenders, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Surveyors and Admins can view tenders
    if (!canManageTenders) {
      navigate('/projects');
      return;
    }
    
    let isMounted = true;
    
    const loadAllData = async () => {
      try {
        const [tenderRes, adminsRes, engineersRes, architectsRes, suppliersRes, templatesRes] = await Promise.all([
          api.get(`/tenders/${tenderId}`),
          api.get('/users/admins').catch(() => ({ data: [] })),
          api.get('/contacts?category=Structural Engineer').catch(() => ({ data: [] })),
          api.get('/contacts?category=Architect').catch(() => ({ data: [] })),
          api.get('/contacts?category=Supplier').catch(() => ({ data: [] })),
          api.get('/email-templates').catch(() => ({ data: [] }))
        ]);
        
        if (!isMounted) return;
        
        setTender(tenderRes.data);
        setAdminUsers(adminsRes.data);
        setStructuralEngineers(engineersRes.data);
        setArchitects(architectsRes.data);
        setSuppliers(suppliersRes.data);
        setEmailTemplates(templatesRes.data);
      } catch (error) {
        console.error('Failed to fetch tender:', error);
        if (isMounted) {
          toast.error('Failed to load tender');
          navigate('/tenders');
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    
    loadAllData();
    return () => { isMounted = false; };
  }, [tenderId, canManageTenders, api, navigate]);

  // Keep individual fetch functions for manual refresh calls
  const fetchTender = async () => {
    try {
      const response = await api.get(`/tenders/${tenderId}`);
      setTender(response.data);
    } catch (error) {
      console.error('Failed to fetch tender:', error);
      toast.error('Failed to load tender');
      navigate('/tenders');
    } finally {
      setLoading(false);
    }
  };

  const fetchAdminUsers = async () => {
    try {
      const response = await api.get('/users/admins');
      setAdminUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch admin users:', error);
    }
  };

  const fetchStructuralEngineers = async () => {
    try {
      const response = await api.get('/contacts?category=Structural Engineer');
      setStructuralEngineers(response.data);
    } catch (error) {
      console.error('Failed to fetch structural engineers:', error);
    }
  };

  const fetchArchitects = async () => {
    try {
      const response = await api.get('/contacts?category=Architect');
      setArchitects(response.data);
    } catch (error) {
      console.error('Failed to fetch architects:', error);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await api.get('/contacts?category=Supplier');
      setSuppliers(response.data);
    } catch (error) {
      console.error('Failed to fetch suppliers:', error);
    }
  };

  const fetchEmailTemplates = async () => {
    try {
      const response = await api.get('/email-templates');
      setEmailTemplates(response.data);
      // If no templates exist, seed defaults
      if (response.data.length === 0) {
        await api.post('/email-templates/seed-defaults');
        const refreshed = await api.get('/email-templates');
        setEmailTemplates(refreshed.data);
      }
    } catch (error) {
      console.error('Failed to fetch email templates:', error);
    }
  };

  const openEditDialog = () => {
    setEditData({
      tender_type: tender.tender_type || 'DEV',
      client_name: tender.client_name || '',
      telephone: tender.telephone || '',
      email: tender.email || '',
      address: tender.address || '',
      postcode: tender.postcode || '',
      site_address: tender.site_address || '',
      site_postcode: tender.site_postcode || '',
      status: tender.status,
      planning_status: tender.planning_status,
      budget_range: tender.budget_range,
      start_date_window: tender.start_date_window,
      project_types: tender.project_types || [],
      date_received: tender.date_received || '',
      return_date: tender.return_date || '',
      submitted_date: tender.submitted_date || '',
      estimated_labour: tender.estimated_labour || '',
      estimated_materials: tender.estimated_materials || '',
      estimated_plant: tender.estimated_plant || '',
      estimated_subcontractors: tender.estimated_subcontractors || '',
      estimated_prelims: tender.estimated_prelims || '',
      estimated_prosums: tender.estimated_prosums || '',
      estimated_op: tender.estimated_op || '',
      estimator_id: tender.estimator_id || '',
      structural_engineer_id: tender.structural_engineer_id || '',
      architect_id: tender.architect_id || '',
      notes: tender.notes || '',
      loss_reason: tender.loss_reason || '',
      loss_reason_notes: tender.loss_reason_notes || '',
      quote_source: tender.quote_source || 'Manual'
    });
    setSiteAddressSameAsClient(
      !tender.site_address || tender.site_address === tender.address
    );
    setEditDialogOpen(true);
  };

  const handleEditProjectTypeToggle = (type) => {
    setEditData(prev => {
      const currentTypes = prev.project_types || [];
      if (currentTypes.includes(type)) {
        return { ...prev, project_types: currentTypes.filter(t => t !== type) };
      } else {
        return { ...prev, project_types: [...currentTypes, type] };
      }
    });
  };

  // Main update function - checks for status changes that need dialogs
  const handleUpdate = async () => {
    const statusChangedToLost = editData.status === 'Lost' && tender.status !== 'Lost';
    const statusChangedToWon = editData.status === 'Won' && tender.status !== 'Won';
    
    // If status changed to Lost, show feedback survey dialog first
    if (statusChangedToLost) {
      setPendingStatusChange(editData);
      setFeedbackSurveyDialogOpen(true);
      return;
    }
    
    // If status changed to Won, show contract dialog first
    if (statusChangedToWon) {
      setPendingStatusChange(editData);
      setContractDialogOpen(true);
      return;
    }
    
    // Otherwise, proceed with normal update
    await executeUpdate(editData);
  };

  // Execute the actual tender update
  const executeUpdate = async (dataToSave, additionalActions = {}) => {
    setSubmitting(true);
    try {
      const updatePayload = {
        ...dataToSave,
        // When site address toggle is checked, sync site_address with address
        site_address: siteAddressSameAsClient ? dataToSave.address : (dataToSave.site_address || null),
        site_postcode: siteAddressSameAsClient ? dataToSave.postcode : (dataToSave.site_postcode || null),
        estimated_labour: dataToSave.estimated_labour 
          ? parseFloat(dataToSave.estimated_labour) 
          : null,
        estimated_materials: dataToSave.estimated_materials 
          ? parseFloat(dataToSave.estimated_materials) 
          : null,
        estimated_plant: dataToSave.estimated_plant 
          ? parseFloat(dataToSave.estimated_plant) 
          : null,
        estimated_subcontractors: dataToSave.estimated_subcontractors 
          ? parseFloat(dataToSave.estimated_subcontractors) 
          : null,
        estimated_prelims: dataToSave.estimated_prelims 
          ? parseFloat(dataToSave.estimated_prelims) 
          : null,
        estimated_prosums: dataToSave.estimated_prosums 
          ? parseFloat(dataToSave.estimated_prosums) 
          : null,
        estimated_op: dataToSave.estimated_op 
          ? parseFloat(dataToSave.estimated_op) 
          : null,
        structural_engineer_id: dataToSave.structural_engineer_id || null,
        architect_id: dataToSave.architect_id || null,
        return_date: dataToSave.return_date || null,
        loss_reason: dataToSave.status === 'Lost' ? (dataToSave.loss_reason || null) : null,
        loss_reason_notes: dataToSave.status === 'Lost' ? (dataToSave.loss_reason_notes || null) : null,
        quote_source: dataToSave.quote_source || 'Manual',
        submitted_date: dataToSave.submitted_date || null
      };

      await api.put(`/tenders/${tenderId}`, updatePayload);
      
      // Update estimator if changed
      if (dataToSave.estimator_id !== (tender.estimator_id || '')) {
        await api.put(`/tenders/${tenderId}/estimator?estimator_id=${dataToSave.estimator_id || ''}`);
      }
      
      // If status changed to Lost and loss_reason is set, add Activity Note
      if (dataToSave.status === 'Lost' && dataToSave.loss_reason && dataToSave.loss_reason !== tender.loss_reason) {
        const noteText = `Loss reason recorded: ${dataToSave.loss_reason}${dataToSave.loss_reason_notes ? ` — ${dataToSave.loss_reason_notes}` : ''}`;
        await api.post(`/tenders/${tenderId}/notes`, { note: noteText });
      }
      
      // Execute any additional actions (from dialogs)
      if (additionalActions.feedbackSent) {
        await api.put(`/tenders/${tenderId}/feedback`, {
          status: 'Sent',
          sent_date: format(new Date(), 'yyyy-MM-dd'),
          sent_to_email: tender.email
        });
        await api.post(`/tenders/${tenderId}/notes`, { 
          note: `Feedback survey sent to ${tender.email}` 
        });
      }
      
      if (additionalActions.feedbackSkipped) {
        await api.post(`/tenders/${tenderId}/notes`, { 
          note: `Feedback survey skipped by ${user?.name || 'user'}` 
        });
      }
      
      if (additionalActions.contractRequested) {
        await api.put(`/tenders/${tenderId}`, { contract_status: 'Generation Requested' });
        await api.post(`/tenders/${tenderId}/notes`, { 
          note: `Contract and H&S generation requested by ${user?.name || 'user'}` 
        });
      }
      
      if (additionalActions.contractSkipped) {
        await api.post(`/tenders/${tenderId}/notes`, { 
          note: `Contract generation skipped by ${user?.name || 'user'}` 
        });
      }
      
      toast.success('Tender updated successfully');
      
      // If status changed to Won, show additional message
      if (dataToSave.status === 'Won' && tender.status !== 'Won') {
        toast.success('New project created from this tender!');
      }
      
      setEditDialogOpen(false);
      setPendingStatusChange(null);
      fetchTender();
    } catch (error) {
      console.error('Failed to update tender:', error);
      // Handle FastAPI validation errors (which come as arrays)
      const detail = error.response?.data?.detail;
      let errorMessage = 'Failed to update tender';
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        errorMessage = detail[0]?.msg || detail[0]?.message || errorMessage;
      }
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle feedback survey dialog actions
  const handleFeedbackSurveyConfirm = async () => {
    setFeedbackSurveyDialogOpen(false);
    await executeUpdate(pendingStatusChange, { feedbackSent: true });
  };

  const handleFeedbackSurveySkip = async () => {
    setFeedbackSurveyDialogOpen(false);
    await executeUpdate(pendingStatusChange, { feedbackSkipped: true });
  };

  // Handle contract dialog actions
  const handleContractConfirm = async () => {
    setContractDialogOpen(false);
    await executeUpdate(pendingStatusChange, { contractRequested: true });
  };

  const handleContractSkip = async () => {
    setContractDialogOpen(false);
    await executeUpdate(pendingStatusChange, { contractSkipped: true });
  };

  // Generate Tender button handler
  const handleGenerateTenderClick = () => {
    // Validation 1: Check project type is selected
    const projectTypes = tender.project_types || [];
    if (projectTypes.length === 0) {
      toast.error('Please select a project type before generating a tender.');
      return;
    }
    
    // Validation 2: Check client name and address are populated
    if (!tender.client_name || !tender.client_name.trim()) {
      toast.error('Please complete client details before generating a tender.');
      return;
    }
    if (!tender.address || !tender.address.trim()) {
      toast.error('Please complete client details before generating a tender.');
      return;
    }
    
    // All validations passed - show confirmation dialog
    setGenerateTenderDialogOpen(true);
  };

  const handleGenerateTenderConfirm = async () => {
    setSubmitting(true);
    try {
      // Update ai_generation_status
      await api.put(`/tenders/${tenderId}`, { 
        ai_generation_status: 'Generation Requested' 
      });
      
      // Add activity note
      await api.post(`/tenders/${tenderId}/notes`, { 
        note: `Tender generation requested by ${user?.name || 'user'}` 
      });
      
      toast.success('Tender generation requested');
      setGenerateTenderDialogOpen(false);
      fetchTender();
    } catch (error) {
      toast.error('Failed to request tender generation');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClearGenerationStatus = async () => {
    try {
      await api.put(`/tenders/${tenderId}`, { 
        ai_generation_status: null 
      });
      toast.success('Generation status cleared');
      fetchTender();
    } catch (error) {
      toast.error('Failed to clear status');
    }
  };

  const handleArchive = async () => {
    try {
      await api.delete(`/tenders/${tenderId}?action=archive`);
      toast.success('Tender archived');
      navigate('/tenders');
    } catch (error) {
      toast.error('Failed to archive tender');
    }
  };

  const handleVoid = async () => {
    try {
      await api.delete(`/tenders/${tenderId}?action=void`);
      toast.success('Tender voided');
      navigate('/tenders');
    } catch (error) {
      toast.error('Failed to void tender');
    }
  };

  const handlePermanentDelete = async () => {
    try {
      await api.delete(`/tenders/${tenderId}?action=delete`);
      toast.success('Tender deleted permanently');
      navigate('/tenders');
    } catch (error) {
      toast.error('Failed to delete tender');
    }
  };

  const handleAddNote = async () => {
    if (!newNoteContent.trim()) {
      toast.error('Please enter a note');
      return;
    }

    setSubmitting(true);
    try {
      await api.post(`/tenders/${tenderId}/notes`, { content: newNoteContent });
      toast.success('Note added');
      setNoteDialogOpen(false);
      setNewNoteContent('');
      fetchTender();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add note');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!confirm('Delete this note?')) return;
    
    try {
      await api.delete(`/tenders/${tenderId}/notes/${noteId}`);
      toast.success('Note deleted');
      fetchTender();
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };

  // Enquiry handlers
  const handleAddEnquiry = async () => {
    if (!newEnquiry.supplier_name.trim() || !newEnquiry.description.trim()) {
      toast.error('Please fill in supplier name and description');
      return;
    }

    setSubmitting(true);
    try {
      await api.post(`/tenders/${tenderId}/enquiries`, newEnquiry);
      toast.success('Enquiry added');
      setEnquiryDialogOpen(false);
      setNewEnquiry({
        supplier_id: '',
        supplier_name: '',
        supplier_email: '',
        trade: '',
        description: '',
        date_sent: format(new Date(), 'yyyy-MM-dd'),
        status: 'Pending'
      });
      fetchTender();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add enquiry');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle supplier selection - auto-fill fields
  const handleSupplierSelect = (supplierId) => {
    if (!supplierId || supplierId === 'manual') {
      setNewEnquiry(prev => ({
        ...prev,
        supplier_id: '',
        supplier_name: '',
        supplier_email: '',
        trade: ''
      }));
      return;
    }
    
    const supplier = suppliers.find(s => s.id === supplierId);
    if (supplier) {
      setNewEnquiry(prev => ({
        ...prev,
        supplier_id: supplier.id,
        supplier_name: supplier.company || supplier.name,
        supplier_email: supplier.email || '',
        trade: supplier.trade || ''
      }));
    }
  };

  // Generate mailto link with template
  const generateMailtoLink = (enquiry, template) => {
    if (!template || !enquiry.supplier_email) return null;
    
    // Replace placeholders in template
    const projectTypes = Array.isArray(tender.project_types) ? tender.project_types.join(', ') : tender.project_types || '';
    
    let subject = template.subject
      .replace('{tender_ref}', tender.tender_ref)
      .replace('{trade}', enquiry.trade || 'General')
      .replace('{project_types}', projectTypes);
    
    let body = template.body
      .replace('{supplier_name}', enquiry.supplier_name)
      .replace('{tender_ref}', tender.tender_ref)
      .replace('{address}', tender.site_address || tender.address)
      .replace('{postcode}', tender.site_postcode || tender.postcode)
      .replace('{project_types}', projectTypes)
      .replace('{trade}', enquiry.trade || 'General')
      .replace('{description}', enquiry.description)
      .replace('{user_name}', user?.name || 'LUX Team');
    
    return `mailto:${enquiry.supplier_email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  // Log email sent
  const handleEmailClick = async (enquiry, template) => {
    try {
      // Log the email
      await api.post(`/tenders/${tenderId}/enquiries/${enquiry.id}/log-email`, {
        template_name: template.name
      });
      toast.success('Email logged - Opening Outlook...');
      fetchTender();
    } catch (error) {
      console.error('Failed to log email:', error);
    }
  };

  // Open email dialog
  const openEmailDialog = (enquiry) => {
    setSelectedEmailEnquiry(enquiry);
    setSelectedTemplate(emailTemplates.length > 0 ? emailTemplates[0].id : '');
    setEmailDialogOpen(true);
  };

  const handleUpdateEnquiry = async () => {
    if (!editingEnquiry) return;
    
    setSubmitting(true);
    try {
      await api.put(`/tenders/${tenderId}/enquiries/${editingEnquiry.id}`, {
        supplier_name: editingEnquiry.supplier_name,
        description: editingEnquiry.description,
        date_sent: editingEnquiry.date_sent,
        status: editingEnquiry.status,
        response_notes: editingEnquiry.response_notes
      });
      toast.success('Enquiry updated');
      setEditEnquiryDialogOpen(false);
      setEditingEnquiry(null);
      fetchTender();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update enquiry');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteEnquiry = async (enquiryId) => {
    if (!confirm('Delete this enquiry?')) return;
    
    try {
      await api.delete(`/tenders/${tenderId}/enquiries/${enquiryId}`);
      toast.success('Enquiry deleted');
      fetchTender();
    } catch (error) {
      toast.error('Failed to delete enquiry');
    }
  };

  const openEditEnquiry = (enquiry) => {
    setEditingEnquiry({ ...enquiry });
    setEditEnquiryDialogOpen(true);
  };

  // Estimator handler
  const handleAssignEstimator = async (estimatorId) => {
    try {
      await api.put(`/tenders/${tenderId}/estimator?estimator_id=${estimatorId || ''}`);
      toast.success(estimatorId ? 'Estimator assigned' : 'Estimator removed');
      fetchTender();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign estimator');
    }
  };

  // ============== NEW FEATURE HANDLERS ==============
  // These handlers are called by extracted components

  // Cost Breakdown save handler (called by CostBreakdownSection)
  const handleSaveCosts = async (costData) => {
    setSubmitting(true);
    try {
      await api.put(`/tenders/${tenderId}/costs`, costData);
      toast.success('Cost breakdown updated');
      fetchTender();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update costs');
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  // Programme Dates save handler (called by ProgrammeDatesSection)
  const handleSaveProgramme = async (programmeData) => {
    setSubmitting(true);
    try {
      await api.put(`/tenders/${tenderId}/programme`, programmeData);
      toast.success('Programme dates updated');
      fetchTender();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update programme');
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  // Hours Per Trade save handler (called by HoursPerTradeSection)
  const handleSaveHours = async (hoursData) => {
    setSubmitting(true);
    try {
      await api.put(`/tenders/${tenderId}/hours-per-trade`, hoursData);
      toast.success('Hours per trade updated');
      fetchTender();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update hours');
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  // Feedback Survey save handler (called by FeedbackSurveySection)
  const handleSaveFeedback = async (feedbackData) => {
    setSubmitting(true);
    try {
      await api.put(`/tenders/${tenderId}/feedback`, feedbackData);
      toast.success('Feedback survey updated');
      fetchTender();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update feedback');
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  // Mark feedback as sent (called by FeedbackSurveySection)
  const handleMarkFeedbackSent = async () => {
    await handleSaveFeedback({
      status: 'Sent',
      sent_date: format(new Date(), 'yyyy-MM-dd'),
      sent_to_email: tender.email,
      feedback_notes: tender.feedback_survey?.feedback_notes || null
    });
  };

  // Create folder structure in OneDrive (manual override button)
  const handleCreateFolderStructure = async () => {
    setSubmitting(true);
    try {
      const response = await api.post(`/tenders/${tenderId}/create-folder-structure`);
      const data = response.data;
      toast.success(
        `Folder structure created successfully. ${data.folders_created} folders created, ${data.folders_existed} already existed.`
      );
      fetchTender(); // Refresh to update folder_structure_created status
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create folder structure');
    } finally {
      setSubmitting(false);
    }
  };

  // Legacy handlers kept for backward compatibility with dialogs
  // Cost Breakdown Inline Edit handlers
  const startCostEdit = () => {
    setCostEditData({
      estimated_labour: tender.estimated_labour || '',
      estimated_materials: tender.estimated_materials || '',
      estimated_plant: tender.estimated_plant || '',
      estimated_subcontractors: tender.estimated_subcontractors || '',
      estimated_prelims: tender.estimated_prelims || '',
      estimated_prosums: tender.estimated_prosums || '',
      estimated_op: tender.estimated_op || ''
    });
    setCostEditMode(true);
  };

  const cancelCostEdit = () => {
    setCostEditMode(false);
    setCostEditData({});
  };

  const saveCostEdit = async () => {
    setSubmitting(true);
    try {
      await api.put(`/tenders/${tenderId}/costs`, {
        estimated_labour: costEditData.estimated_labour ? parseFloat(costEditData.estimated_labour) : null,
        estimated_materials: costEditData.estimated_materials ? parseFloat(costEditData.estimated_materials) : null,
        estimated_plant: costEditData.estimated_plant ? parseFloat(costEditData.estimated_plant) : null,
        estimated_subcontractors: costEditData.estimated_subcontractors ? parseFloat(costEditData.estimated_subcontractors) : null,
        estimated_prelims: costEditData.estimated_prelims ? parseFloat(costEditData.estimated_prelims) : null,
        estimated_prosums: costEditData.estimated_prosums ? parseFloat(costEditData.estimated_prosums) : null,
        estimated_op: costEditData.estimated_op ? parseFloat(costEditData.estimated_op) : null
      });
      toast.success('Cost breakdown updated');
      setCostEditMode(false);
      fetchTender();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update costs');
    } finally {
      setSubmitting(false);
    }
  };

  // Programme Dates handlers
  const startProgrammeEdit = () => {
    setProgrammeData({
      proposed_start_date: tender.programme_dates?.proposed_start_date || '',
      estimated_duration_weeks: tender.programme_dates?.estimated_duration_weeks || '',
      proposed_completion_date: tender.programme_dates?.proposed_completion_date || '',
      notes: tender.programme_dates?.notes || ''
    });
    setProgrammeEditMode(true);
  };

  const cancelProgrammeEdit = () => {
    setProgrammeEditMode(false);
    setProgrammeData({});
  };

  const saveProgrammeEdit = async () => {
    setSubmitting(true);
    try {
      await api.put(`/tenders/${tenderId}/programme`, {
        proposed_start_date: programmeData.proposed_start_date || null,
        estimated_duration_weeks: programmeData.estimated_duration_weeks ? parseInt(programmeData.estimated_duration_weeks) : null,
        proposed_completion_date: programmeData.proposed_completion_date || null,
        notes: programmeData.notes || null
      });
      toast.success('Programme dates updated');
      setProgrammeEditMode(false);
      fetchTender();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update programme');
    } finally {
      setSubmitting(false);
    }
  };

  // Hours Per Trade handlers
  const startHoursEdit = () => {
    const existingHours = tender.hours_per_trade || [];
    setHoursData(existingHours.length > 0 ? [...existingHours] : [
      { trade: '', hours_allowed: '', labour_rate: '', actual_hours: '' }
    ]);
    setHoursEditMode(true);
  };

  const cancelHoursEdit = () => {
    setHoursEditMode(false);
    setHoursData([]);
  };

  const addHoursRow = () => {
    setHoursData([...hoursData, { trade: '', hours_allowed: '', labour_rate: '', actual_hours: '' }]);
  };

  const removeHoursRow = (index) => {
    setHoursData(hoursData.filter((_, i) => i !== index));
  };

  const updateHoursRow = (index, field, value) => {
    const newData = [...hoursData];
    newData[index] = { ...newData[index], [field]: value };
    setHoursData(newData);
  };

  const saveHoursEdit = async () => {
    // Filter out empty rows
    const validRows = hoursData.filter(row => row.trade && row.trade.trim());
    if (validRows.length === 0) {
      toast.error('Please add at least one trade');
      return;
    }

    setSubmitting(true);
    try {
      await api.put(`/tenders/${tenderId}/hours-per-trade`, {
        hours_per_trade: validRows.map(row => ({
          trade: row.trade,
          hours_allowed: row.hours_allowed ? parseFloat(row.hours_allowed) : null,
          labour_rate: row.labour_rate ? parseFloat(row.labour_rate) : null,
          actual_hours: row.actual_hours ? parseFloat(row.actual_hours) : null
        }))
      });
      toast.success('Hours per trade updated');
      setHoursEditMode(false);
      fetchTender();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update hours');
    } finally {
      setSubmitting(false);
    }
  };

  // Feedback Survey handlers
  const saveFeedbackSurvey = async (newData) => {
    setSubmitting(true);
    try {
      await api.put(`/tenders/${tenderId}/feedback`, newData);
      toast.success('Feedback survey updated');
      fetchTender();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update feedback');
    } finally {
      setSubmitting(false);
    }
  };

  const markFeedbackSent = async () => {
    await saveFeedbackSurvey({
      status: 'Sent',
      sent_date: format(new Date(), 'yyyy-MM-dd'),
      sent_to_email: tender.email,
      feedback_notes: tender.feedback_survey?.feedback_notes || null
    });
  };

  // Loss Reason inline edit handlers
  const startLossReasonEdit = () => {
    setLossReasonData({
      loss_reason: tender.loss_reason || '',
      loss_reason_notes: tender.loss_reason_notes || ''
    });
    setLossReasonEditMode(true);
  };

  const cancelLossReasonEdit = () => {
    setLossReasonEditMode(false);
    setLossReasonData({ loss_reason: '', loss_reason_notes: '' });
  };

  const saveLossReasonEdit = async () => {
    setSubmitting(true);
    try {
      await api.put(`/tenders/${tenderId}`, {
        loss_reason: lossReasonData.loss_reason || null,
        loss_reason_notes: lossReasonData.loss_reason_notes || null
      });
      
      // Add activity note
      await api.post(`/tenders/${tenderId}/notes`, {
        note: `Loss reason updated: ${lossReasonData.loss_reason || 'Cleared'}${lossReasonData.loss_reason_notes ? ` — ${lossReasonData.loss_reason_notes}` : ''}`
      });
      
      toast.success('Loss reason updated');
      setLossReasonEditMode(false);
      fetchTender();
    } catch (error) {
      const detail = error.response?.data?.detail;
      let errorMessage = 'Failed to update loss reason';
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        errorMessage = detail[0]?.msg || detail[0]?.message || errorMessage;
      }
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (value) => {
    if (!value && value !== 0) return '-';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const getDaysOpen = () => {
    if (!tender) return 0;
    return differenceInDays(new Date(), parseISO(tender.submission_date));
  };

  // Allow admins and surveyors to view (surveyors can create tenders but not edit/delete)
  if (!canManageTenders) return null;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#F5A623]" />
      </div>
    );
  }

  if (!tender) {
    return null;
  }

  const isArchived = tender.status === 'Archived' || tender.status === 'Void';

  return (
    <div className="container mx-auto px-4 py-6 md:py-8 max-w-4xl">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/tenders')}
            className="text-gray-400 hover:text-white mb-2"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Tenders
          </Button>
          <h1 
            className="text-2xl md:text-3xl font-bold text-[#F5A623]"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            data-testid="tender-ref"
          >
            {tender.tender_ref}
          </h1>
          <div className="flex items-center gap-2 mt-2">
            <Badge className={STATUS_COLORS[tender.status] || STATUS_COLORS['Processing']}>
              {tender.status}
            </Badge>
            {tender.is_overdue && (
              <Badge className="bg-red-500/20 text-red-400 border-red-500/30 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                Overdue
              </Badge>
            )}
          </div>
        </div>

        <div className="flex gap-2">
          {!isArchived && canEditRecords && (
            <>
              {/* Generate Tender Button - Item 6 */}
              <Button
                onClick={handleGenerateTenderClick}
                disabled={tender.ai_generation_status === 'Generation Requested'}
                className={`${
                  tender.ai_generation_status === 'Generation Requested'
                    ? 'bg-cyan-500/50 cursor-not-allowed'
                    : 'bg-cyan-500 hover:bg-cyan-600'
                } text-white`}
                data-testid="generate-tender-btn"
              >
                {tender.ai_generation_status === 'Generation Requested' ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generation Requested
                  </>
                ) : tender.ai_generation_status ? (
                  <>
                    <ClipboardList className="w-4 h-4 mr-2" />
                    Regenerate Tender
                  </>
                ) : (
                  <>
                    <ClipboardList className="w-4 h-4 mr-2" />
                    Generate Tender
                  </>
                )}
              </Button>
              
              <Button
                onClick={openEditDialog}
                data-testid="edit-tender-btn"
                className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D]"
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit
              </Button>
              
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" className="border-white/20 text-gray-300">
                    <Archive className="w-4 h-4 mr-2" />
                    Archive
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-[#2A3240] border-white/10">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="text-white">Archive Tender</AlertDialogTitle>
                    <AlertDialogDescription className="text-gray-400">
                      This will archive the tender. It will no longer appear in the active list but can be found using filters.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="border-white/20 text-gray-300">Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleArchive} className="bg-[#F5A623] text-[#1E2530]">
                      Archive
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10">
                    <Ban className="w-4 h-4 mr-2" />
                    Void
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-[#2A3240] border-white/10">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="text-white">Void Tender</AlertDialogTitle>
                    <AlertDialogDescription className="text-gray-400">
                      This will mark the tender as void (no longer required). This action indicates the tender enquiry is no longer valid.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="border-white/20 text-gray-300">Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleVoid} className="bg-red-500 text-white hover:bg-red-600">
                      Void Tender
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" className="border-red-700/30 text-red-500 hover:bg-red-700/10" data-testid="delete-tender-btn">
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-[#2A3240] border-white/10">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="text-white">Delete Tender</AlertDialogTitle>
                    <AlertDialogDescription className="text-gray-400">
                      This tender will be moved to trash and can be restored within 30 days. 
                      <span className="text-amber-400 block mt-2">OneDrive files will be preserved.</span>
                      <span className="text-amber-400 block mt-2">After 30 days, the tender will be permanently deleted.</span>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="border-white/20 text-gray-300">Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handlePermanentDelete} className="bg-red-700 text-white hover:bg-red-800">
                      Move to Trash
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          )}
        </div>
      </div>

      {/* Overdue Warning Banner */}
      {tender.is_overdue && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
          <div>
            <p className="text-red-400 font-medium">This tender has been open for more than 3 weeks</p>
            <p className="text-gray-400 text-sm">Please review and update the status. Days open: {getDaysOpen()}</p>
          </div>
        </div>
      )}

      {/* Converted Project Link - only show when status is Won AND project exists */}
      {tender.status === 'Won' && tender.converted_project_id && (
        <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-green-400" />
            <div>
              <p className="text-green-400 font-medium">Tender Won - Project Created</p>
              <p className="text-gray-400 text-sm">A project has been created from this successful tender.</p>
            </div>
          </div>
          <Link to={`/projects/${tender.converted_project_id}`}>
            <Button variant="outline" size="sm" className="border-green-500/30 text-green-400 hover:bg-green-500/10">
              <ExternalLink className="w-4 h-4 mr-2" />
              View Project
            </Button>
          </Link>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Client & Contact Information */}
        <Card className="bg-[#2A3240] border-white/10">
          <CardHeader className="pb-3">
            <CardTitle className="text-white flex items-center gap-2 text-lg">
              <User className="w-5 h-5 text-[#F5A623]" />
              Client & Contact
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label className="text-gray-500 text-xs uppercase">Client Name</Label>
              <p className="text-white font-medium">{tender.client_name}</p>
            </div>
            <div className="flex items-center gap-2 text-gray-300">
              <Phone className="w-4 h-4 text-gray-500" />
              <a href={`tel:${tender.telephone}`} className="hover:text-[#F5A623]">{tender.telephone}</a>
            </div>
            <div className="flex items-center gap-2 text-gray-300">
              <Mail className="w-4 h-4 text-gray-500" />
              <a href={`mailto:${tender.email}`} className="hover:text-[#F5A623]">{tender.email}</a>
            </div>
          </CardContent>
        </Card>

        {/* Address Information */}
        <Card className="bg-[#2A3240] border-white/10">
          <CardHeader className="pb-3">
            <CardTitle className="text-white flex items-center gap-2 text-lg">
              <MapPin className="w-5 h-5 text-[#F5A623]" />
              Addresses
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label className="text-gray-500 text-xs uppercase">Client Address</Label>
              <p className="text-white">{tender.address}</p>
              <p className="text-gray-400">{tender.postcode}</p>
            </div>
            {tender.site_address && tender.site_address !== tender.address && (
              <div className="pt-2 border-t border-white/10">
                <Label className="text-gray-500 text-xs uppercase">Site Address</Label>
                <p className="text-white">{tender.site_address}</p>
                <p className="text-gray-400">{tender.site_postcode}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Project Details */}
        <Card className="bg-[#2A3240] border-white/10">
          <CardHeader className="pb-3">
            <CardTitle className="text-white flex items-center gap-2 text-lg">
              <Building2 className="w-5 h-5 text-[#F5A623]" />
              Project Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-gray-500 text-xs uppercase">Project Type(s)</Label>
                <p className="text-white">
                  {Array.isArray(tender.project_types) ? tender.project_types.join(', ') : tender.project_types || '-'}
                </p>
                {/* Primary Type - for reporting allocation */}
                {tender.primary_project_type && Array.isArray(tender.project_types) && tender.project_types.length > 1 && (
                  <p className="text-xs text-[#F5A623] mt-1 flex items-center gap-1">
                    <span className="text-gray-500">Primary Type:</span> {tender.primary_project_type}
                  </p>
                )}
              </div>
              <div>
                <Label className="text-gray-500 text-xs uppercase">Planning Status</Label>
                <p className="text-white">{tender.planning_status}</p>
              </div>
              <div>
                <Label className="text-gray-500 text-xs uppercase">Budget Range</Label>
                <p className="text-white">{tender.budget_range}</p>
              </div>
              <div>
                <Label className="text-gray-500 text-xs uppercase">Start Window</Label>
                <p className="text-white">{tender.start_date_window}</p>
              </div>
            </div>
            
            {/* Cost Breakdown Section - Using extracted component */}
            <CostBreakdownSection
              tender={tender}
              canEdit={canEditRecords}
              isArchived={isArchived}
              onSave={handleSaveCosts}
              submitting={submitting}
            />
          </CardContent>
        </Card>

        {/* Status & Dates */}
        <Card className="bg-[#2A3240] border-white/10">
          <CardHeader className="pb-3">
            <CardTitle className="text-white flex items-center gap-2 text-lg">
              <Clock className="w-5 h-5 text-[#F5A623]" />
              Status & Dates
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label className="text-gray-500 text-xs uppercase">Current Status</Label>
              <div className="mt-1">
                <Badge className={`${STATUS_COLORS[tender.status]} text-sm`}>
                  {tender.status}
                </Badge>
              </div>
              {/* Loss Reason Display */}
              {tender.status === 'Lost' && tender.loss_reason && (
                <div className="mt-2 p-2 bg-red-500/10 border border-red-500/30 rounded">
                  <p className="text-xs text-gray-400">Loss Reason</p>
                  <p className="text-sm text-red-400">{tender.loss_reason}</p>
                </div>
              )}
            </div>

            {/* Quote Source Display */}
            <div className="pt-2 border-t border-white/10">
              <Label className="text-gray-500 text-xs uppercase">Quote Source</Label>
              <div className="mt-1">
                <Badge variant="outline" className={`text-xs ${
                  tender.quote_source === 'AI Generated' ? 'border-cyan-500/50 text-cyan-400' :
                  tender.quote_source === 'Hybrid' ? 'border-purple-500/50 text-purple-400' :
                  'border-white/20 text-gray-400'
                }`}>
                  {tender.quote_source || 'Manual'}
                </Badge>
              </div>
            </div>
            
            {/* Estimator Display */}
            <div className="pt-2 border-t border-white/10">
              <Label className="text-gray-500 text-xs uppercase flex items-center gap-1">
                <UserCog className="w-3 h-3" />
                Estimator
              </Label>
              <p className="text-white mt-1">{tender.estimator_name || 'Not assigned'}</p>
            </div>

            {/* Structural Engineer Display */}
            <div className="pt-2 border-t border-white/10">
              <Label className="text-gray-500 text-xs uppercase flex items-center gap-1">
                <Building2 className="w-3 h-3" />
                Structural Engineer
              </Label>
              <p className="text-white mt-1">{tender.structural_engineer_name || 'Not assigned'}</p>
            </div>

            {/* Architect Display */}
            <div className="pt-2 border-t border-white/10">
              <Label className="text-gray-500 text-xs uppercase flex items-center gap-1">
                <Building2 className="w-3 h-3" />
                Architect
              </Label>
              <p className="text-white mt-1">{tender.architect_name || 'Not assigned'}</p>
            </div>

            <div className="grid grid-cols-2 gap-3 pt-2 border-t border-white/10">
              <div>
                <Label className="text-gray-500 text-xs uppercase">Date Received</Label>
                <p className="text-white">{tender.date_received ? format(parseISO(tender.date_received), 'dd MMM yyyy') : '-'}</p>
              </div>
              <div>
                <Label className="text-gray-500 text-xs uppercase">Return Date</Label>
                {tender.return_date ? (
                  <p className={`${
                    new Date(tender.return_date) < new Date() ? 'text-red-400' :
                    (new Date(tender.return_date) - new Date()) / (1000 * 60 * 60 * 24) <= 7 ? 'text-amber-400' :
                    'text-white'
                  }`}>
                    {format(parseISO(tender.return_date), 'dd MMM yyyy')}
                    {new Date(tender.return_date) < new Date() && <span className="ml-2 text-xs">(Overdue)</span>}
                  </p>
                ) : (
                  <p className="text-gray-500">-</p>
                )}
              </div>
              <div>
                <Label className="text-gray-500 text-xs uppercase">Submitted Date</Label>
                <p className={`${tender.submitted_date ? 'text-purple-400' : 'text-gray-500'}`}>
                  {tender.submitted_date ? format(parseISO(tender.submitted_date), 'dd MMM yyyy') : '-'}
                </p>
              </div>
              <div>
                <Label className="text-gray-500 text-xs uppercase">Created</Label>
                <p className="text-gray-400 text-sm">{format(parseISO(tender.created_at), 'dd MMM yyyy HH:mm')}</p>
              </div>
              <div>
                <Label className="text-gray-500 text-xs uppercase">Last Updated</Label>
                <p className="text-gray-400 text-sm">{format(parseISO(tender.updated_at), 'dd MMM yyyy HH:mm')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Programme Dates Section - Using extracted component */}
      <ProgrammeDatesSection
        tender={tender}
        canEdit={canEditRecords}
        isArchived={isArchived}
        onSave={handleSaveProgramme}
        submitting={submitting}
      />

      {/* Hours Per Trade Section - Using extracted component */}
      <HoursPerTradeSection
        tender={tender}
        canEdit={canEditRecords}
        isArchived={isArchived}
        onSave={handleSaveHours}
        submitting={submitting}
      />

      {/* Loss Reason Section - Only for Lost Tenders (above Feedback Survey) */}
      {tender.status === 'Lost' && (
        <Card className="bg-[#2A3240] border-white/10 mt-6 border-l-4 border-l-red-500">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-white flex items-center gap-2 text-lg">
                <AlertTriangle className="w-5 h-5 text-red-400" />
                Loss Reason
                {tender.loss_reason && (
                  <Badge className="ml-2 text-xs bg-red-500/20 text-red-400 border-red-500/30">
                    {tender.loss_reason}
                  </Badge>
                )}
              </CardTitle>
              {!isArchived && canEditRecords && !lossReasonEditMode && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={startLossReasonEdit}
                  className="text-gray-400 hover:text-[#F5A623] h-7"
                  data-testid="edit-loss-reason-btn"
                >
                  <Edit className="w-3 h-3 mr-1" />
                  Edit
                </Button>
              )}
              {lossReasonEditMode && (
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={cancelLossReasonEdit}
                    className="text-gray-400 hover:text-white h-7"
                  >
                    <X className="w-3 h-3 mr-1" />
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={saveLossReasonEdit}
                    disabled={submitting}
                    className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] h-7"
                    data-testid="save-loss-reason-btn"
                  >
                    {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Save className="w-3 h-3 mr-1" />Save</>}
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-gray-400 text-sm mb-4">Record why this tender was lost to help improve future quotes.</p>
            
            {lossReasonEditMode ? (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-gray-300 text-sm">Loss Reason</Label>
                  <Select
                    value={lossReasonData.loss_reason || ''}
                    onValueChange={(v) => setLossReasonData(prev => ({ ...prev, loss_reason: v }))}
                  >
                    <SelectTrigger className="bg-[#1E2530] border-white/20 text-white">
                      <SelectValue placeholder="Select reason for loss" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#2A3240] border-white/10">
                      <SelectItem value="Price — significantly higher" className="text-white focus:bg-[#F5A623]/20">Price — significantly higher</SelectItem>
                      <SelectItem value="Price — slightly higher" className="text-white focus:bg-[#F5A623]/20">Price — slightly higher</SelectItem>
                      <SelectItem value="Went with known contractor" className="text-white focus:bg-[#F5A623]/20">Went with known contractor</SelectItem>
                      <SelectItem value="Client changed plans" className="text-white focus:bg-[#F5A623]/20">Client changed plans</SelectItem>
                      <SelectItem value="No feedback received" className="text-white focus:bg-[#F5A623]/20">No feedback received</SelectItem>
                      <SelectItem value="Other" className="text-white focus:bg-[#F5A623]/20">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300 text-sm">Additional Notes</Label>
                  <Textarea
                    value={lossReasonData.loss_reason_notes || ''}
                    onChange={(e) => setLossReasonData(prev => ({ ...prev, loss_reason_notes: e.target.value }))}
                    className="bg-[#1E2530] border-white/20 text-white resize-none"
                    placeholder="Additional context about the loss..."
                    rows={2}
                  />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-3 bg-[#1E2530] rounded-lg">
                  <Label className="text-gray-500 text-xs uppercase">Reason</Label>
                  <p className="text-white mt-1">
                    {tender.loss_reason || <span className="text-gray-500 italic">Not recorded</span>}
                  </p>
                </div>
                <div className="p-3 bg-[#1E2530] rounded-lg">
                  <Label className="text-gray-500 text-xs uppercase">Notes</Label>
                  <p className="text-gray-300 mt-1 text-sm">
                    {tender.loss_reason_notes || <span className="text-gray-500 italic">No notes</span>}
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Feedback Survey Section - Using extracted component */}
      <FeedbackSurveySection
        tender={tender}
        canEdit={canEditRecords}
        isArchived={isArchived}
        feedbackData={feedbackData}
        setFeedbackData={setFeedbackData}
        onSave={handleSaveFeedback}
        onMarkSent={handleMarkFeedbackSent}
        submitting={submitting}
      />

      {/* Activity Notes Section */}
      <Card className="bg-[#2A3240] border-white/10 mt-6">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-white flex items-center gap-2 text-lg">
              <StickyNote className="w-5 h-5 text-[#F5A623]" />
              Activity Notes
            </CardTitle>
            {!isArchived && (
              <Button 
                onClick={() => setNoteDialogOpen(true)}
                size="sm"
                data-testid="add-note-btn"
                className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D]"
              >
                <MessageSquarePlus className="w-4 h-4 mr-2" />
                Add Note
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {/* Legacy single note (if exists) */}
          {tender.notes && (
            <div className="mb-4 p-3 bg-[#1E2530] rounded-lg border border-white/5">
              <p className="text-gray-300 whitespace-pre-wrap text-sm">{tender.notes}</p>
              <p className="text-xs text-gray-500 mt-2">Initial notes</p>
            </div>
          )}
          
          {/* Multiple activity notes */}
          {tender.tender_notes && tender.tender_notes.length > 0 ? (
            <div className="space-y-3">
              {[...tender.tender_notes].reverse().map((note) => (
                <div key={note.id} className="p-3 bg-[#1E2530] rounded-lg border border-white/5 group">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-gray-300 whitespace-pre-wrap text-sm flex-1">{note.content}</p>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteNote(note.id)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-500 hover:text-red-400 h-6 w-6 p-0"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                  <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                    <User className="w-3 h-3" />
                    <span className="text-[#F5A623]">{note.created_by_name}</span>
                    <span>•</span>
                    <span>{format(parseISO(note.created_at), 'dd MMM yyyy HH:mm')}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : !tender.notes && (
            <p className="text-gray-500 text-sm text-center py-4">No notes yet. Click "Add Note" to add one.</p>
          )}
        </CardContent>
      </Card>

      {/* Supplier Enquiries Section */}
      <Card className="bg-[#2A3240] border-white/10 mt-6">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-white flex items-center gap-2 text-lg">
              <Package className="w-5 h-5 text-[#F5A623]" />
              Supplier Enquiries
            </CardTitle>
            {!isArchived && (
              <Button 
                onClick={() => setEnquiryDialogOpen(true)}
                size="sm"
                data-testid="add-enquiry-btn"
                className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D]"
              >
                <Send className="w-4 h-4 mr-2" />
                Add Enquiry
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {tender.enquiries && tender.enquiries.length > 0 ? (
            <div className="space-y-3">
              {[...tender.enquiries].reverse().map((enquiry) => (
                <div key={enquiry.id} className="p-4 bg-[#1E2530] rounded-lg border border-white/5 group">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <div className="flex items-center flex-wrap gap-2 mb-2">
                        <span className="text-white font-medium">{enquiry.supplier_name}</span>
                        {enquiry.trade && (
                          <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 text-xs">
                            {enquiry.trade}
                          </Badge>
                        )}
                        <Badge className={
                          enquiry.status === 'Received' 
                            ? 'bg-green-500/20 text-green-400 border-green-500/30'
                            : enquiry.status === 'No Response'
                            ? 'bg-red-500/20 text-red-400 border-red-500/30'
                            : 'bg-amber-500/20 text-amber-400 border-amber-500/30'
                        }>
                          {enquiry.status}
                        </Badge>
                      </div>
                      <p className="text-gray-300 text-sm">{enquiry.description}</p>
                      {enquiry.response_notes && (
                        <div className="mt-2 p-2 bg-white/5 rounded border-l-2 border-[#F5A623]">
                          <p className="text-xs text-gray-500 mb-1">Response Notes:</p>
                          <p className="text-gray-300 text-sm">{enquiry.response_notes}</p>
                        </div>
                      )}
                      {/* Email sent log */}
                      {enquiry.email_sent_at && (
                        <div className="mt-2 p-2 bg-green-500/10 rounded border-l-2 border-green-500">
                          <p className="text-xs text-green-400 flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            Emailed via {enquiry.email_template_used || 'template'} on {format(parseISO(enquiry.email_sent_at), 'dd MMM yyyy HH:mm')} by {enquiry.email_sent_by_name}
                          </p>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1">
                      {/* Email Supplier button */}
                      {enquiry.supplier_email && !isArchived && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEmailDialog(enquiry)}
                          className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 h-7 w-7 p-0"
                          title="Email Supplier"
                        >
                          <Mail className="w-3 h-3" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditEnquiry(enquiry)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-500 hover:text-[#F5A623] h-7 w-7 p-0"
                      >
                        <Edit className="w-3 h-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteEnquiry(enquiry.id)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-500 hover:text-red-400 h-7 w-7 p-0"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center flex-wrap gap-4 mt-3 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      Created: {format(parseISO(enquiry.date_sent), 'dd MMM yyyy')}
                    </span>
                    <span className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      <span className="text-[#F5A623]">{enquiry.created_by_name}</span>
                    </span>
                    {enquiry.supplier_email && (
                      <span className="flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        {enquiry.supplier_email}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm text-center py-4">No supplier enquiries yet. Click "Add Enquiry" to record one.</p>
          )}
        </CardContent>
      </Card>

      {/* Documents Section */}
      {tender && (
        <Card className="bg-[#2A3240] border-white/10 mt-6">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-white flex items-center gap-2 text-lg">
                <FolderOpen className="w-5 h-5 text-blue-400" />
                Documents
                {/* Folder Status Indicator */}
                {tender.folder_structure_created ? (
                  <span className="flex items-center gap-1 ml-2" title="Folder structure created">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    <span className="text-xs text-green-500">Structure Ready</span>
                  </span>
                ) : (
                  <span className="flex items-center gap-1 ml-2" title="Folder structure not yet created">
                    <span className="w-3 h-3 rounded-full bg-gray-500"></span>
                    <span className="text-xs text-gray-400">No Structure</span>
                  </span>
                )}
              </CardTitle>
              {/* Create Folder Structure Button - Always visible for admins */}
              {isAdmin && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="border-[#F5A623] text-[#F5A623] hover:bg-[#F5A623]/10"
                      data-testid="create-folder-structure-btn"
                    >
                      <FolderOpen className="w-4 h-4 mr-1" />
                      {tender.folder_structure_created ? 'Verify/Repair Folders' : 'Create Folder Structure'}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-[#2A3240] border-white/10">
                    <AlertDialogHeader>
                      <AlertDialogTitle className="text-white">
                        {tender.folder_structure_created ? 'Verify Folder Structure' : 'Create Folder Structure'}
                      </AlertDialogTitle>
                      <AlertDialogDescription className="text-gray-400">
                        This will create any missing folders in your OneDrive structure. 
                        Existing folders and their contents will not be affected.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="border-white/20 text-gray-300">Cancel</AlertDialogCancel>
                      <AlertDialogAction 
                        onClick={handleCreateFolderStructure}
                        className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D]"
                      >
                        Continue
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <FileBrowser
              entityType="tender"
              entityId={tender.id}
              entityRef={tender.tender_ref}
              canUpload={!isArchived && canEditRecords}
              canDelete={isAdmin}
            />
          </CardContent>
        </Card>
      )}

      {/* Add Note Dialog */}
      <Dialog open={noteDialogOpen} onOpenChange={setNoteDialogOpen}>
        <DialogContent className="bg-[#2A3240] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <MessageSquarePlus className="w-5 h-5 text-[#F5A623]" />
              Add Note
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Add an internal note to track activity on this tender.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Note</Label>
              <Textarea
                value={newNoteContent}
                onChange={(e) => setNewNoteContent(e.target.value)}
                placeholder="Enter your note..."
                rows={4}
                data-testid="note-content-input"
                className="bg-[#1E2530] border-white/20 text-white resize-none"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button 
                variant="outline" 
                onClick={() => { setNoteDialogOpen(false); setNewNoteContent(''); }} 
                className="flex-1 border-white/20 text-gray-300"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleAddNote} 
                disabled={submitting || !newNoteContent.trim()}
                data-testid="save-note-btn"
                className="flex-1 bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D]"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Note'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-[#2A3240] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Edit className="w-5 h-5 text-[#F5A623]" />
              Edit Tender
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {/* Client & Contact Section */}
            <div className="space-y-3 p-3 bg-[#1E2530] rounded-lg">
              <Label className="text-[#F5A623] text-sm font-medium">Client & Contact Details</Label>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-gray-300 text-xs">Client Name</Label>
                  <Input
                    value={editData.client_name || ''}
                    onChange={(e) => setEditData(prev => ({ ...prev, client_name: e.target.value }))}
                    placeholder="Client name"
                    data-testid="edit-client-name"
                    className="bg-[#2A3240] border-white/20 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300 text-xs">Telephone</Label>
                  <Input
                    value={editData.telephone || ''}
                    onChange={(e) => setEditData(prev => ({ ...prev, telephone: e.target.value }))}
                    placeholder="Phone number"
                    data-testid="edit-telephone"
                    className="bg-[#2A3240] border-white/20 text-white"
                  />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label className="text-gray-300 text-xs">Email</Label>
                  <Input
                    type="email"
                    value={editData.email || ''}
                    onChange={(e) => setEditData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="Email address"
                    data-testid="edit-email"
                    className="bg-[#2A3240] border-white/20 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300 text-xs">Address</Label>
                  <Input
                    value={editData.address || ''}
                    onChange={(e) => setEditData(prev => ({ ...prev, address: e.target.value }))}
                    placeholder="Address"
                    data-testid="edit-address"
                    className="bg-[#2A3240] border-white/20 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300 text-xs">Postcode</Label>
                  <Input
                    value={editData.postcode || ''}
                    onChange={(e) => setEditData(prev => ({ ...prev, postcode: e.target.value }))}
                    placeholder="Postcode"
                    data-testid="edit-postcode"
                    className="bg-[#2A3240] border-white/20 text-white"
                  />
                </div>
              </div>

              {/* Site Address Toggle */}
              <div className="space-y-3 pt-1">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="site-address-same"
                    checked={siteAddressSameAsClient}
                    onChange={(e) => setSiteAddressSameAsClient(e.target.checked)}
                    className="rounded border-white/20"
                    data-testid="site-address-same-toggle"
                  />
                  <Label htmlFor="site-address-same" className="text-gray-400 text-xs cursor-pointer">
                    Site address same as above
                  </Label>
                </div>
                {!siteAddressSameAsClient && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label className="text-gray-300 text-xs">Site Address</Label>
                      <Input
                        value={editData.site_address || ''}
                        onChange={(e) => setEditData(prev => ({ ...prev, site_address: e.target.value }))}
                        placeholder="Site address (where work is carried out)"
                        data-testid="edit-site-address"
                        className="bg-[#2A3240] border-white/20 text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-300 text-xs">Site Postcode</Label>
                      <Input
                        value={editData.site_postcode || ''}
                        onChange={(e) => setEditData(prev => ({ ...prev, site_postcode: e.target.value.toUpperCase() }))}
                        placeholder="Site postcode"
                        data-testid="edit-site-postcode"
                        className="bg-[#2A3240] border-white/20 text-white"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-gray-300">Tender Type</Label>
                <Select 
                  value={editData.tender_type || 'DEV'} 
                  onValueChange={(v) => setEditData(prev => ({ ...prev, tender_type: v }))}
                >
                  <SelectTrigger className="bg-[#1E2530] border-white/20 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#2A3240] border-white/10">
                    {TENDER_TYPE_OPTIONS.map(option => (
                      <SelectItem key={option.value} value={option.value} className="text-white focus:bg-[#F5A623]/20">
                        {option.value} - {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-300">Status</Label>
                <Select 
                  value={editData.status} 
                  onValueChange={(v) => setEditData(prev => ({ ...prev, status: v }))}
                >
                  <SelectTrigger className="bg-[#1E2530] border-white/20 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#2A3240] border-white/10">
                    {STATUS_OPTIONS.map(status => (
                      <SelectItem key={status} value={status} className="text-white focus:bg-[#F5A623]/20">
                        {status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {editData.status === 'Won' && tender.status !== 'Won' && (
              <p className="text-green-400 text-xs flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" />
                Setting to Won will automatically create a new project
              </p>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-gray-300">Planning Status</Label>
                <Select 
                  value={editData.planning_status} 
                  onValueChange={(v) => setEditData(prev => ({ ...prev, planning_status: v }))}
                >
                  <SelectTrigger className="bg-[#1E2530] border-white/20 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#2A3240] border-white/10">
                    {PLANNING_STATUS_OPTIONS.map(status => (
                      <SelectItem key={status} value={status} className="text-white focus:bg-[#F5A623]/20">
                        {status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-300">Budget Range</Label>
                <Select 
                  value={editData.budget_range} 
                  onValueChange={(v) => setEditData(prev => ({ ...prev, budget_range: v }))}
                >
                  <SelectTrigger className="bg-[#1E2530] border-white/20 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#2A3240] border-white/10">
                    {BUDGET_RANGE_OPTIONS.map(budget => (
                      <SelectItem key={budget} value={budget} className="text-white focus:bg-[#F5A623]/20">
                        {budget}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Project Types - Multi-select */}
            <div className="space-y-2">
              <Label className="text-gray-300">Project Type(s)</Label>
              <div className="grid grid-cols-2 gap-2 p-3 rounded-lg bg-[#1E2530] border border-white/20">
                {PROJECT_TYPE_OPTIONS.map(type => (
                  <label 
                    key={type} 
                    className="flex items-center gap-2 cursor-pointer hover:bg-white/5 p-2 rounded transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={editData.project_types?.includes(type) || false}
                      onChange={() => handleEditProjectTypeToggle(type)}
                      className="w-4 h-4 rounded border-white/20 bg-[#2A3240] text-[#F5A623] focus:ring-[#F5A623] focus:ring-offset-0"
                    />
                    <span className="text-white text-sm">{type}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-gray-300">Start Date Window</Label>
                <Select 
                  value={editData.start_date_window} 
                  onValueChange={(v) => setEditData(prev => ({ ...prev, start_date_window: v }))}
                >
                  <SelectTrigger className="bg-[#1E2530] border-white/20 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#2A3240] border-white/10">
                    {START_DATE_OPTIONS.map(window => (
                      <SelectItem key={window} value={window} className="text-white focus:bg-[#F5A623]/20">
                        {window}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Date Received</Label>
                <Input
                  type="date"
                  value={editData.date_received || ''}
                  onChange={(e) => setEditData(prev => ({ ...prev, date_received: e.target.value }))}
                  className="bg-[#1E2530] border-white/20 text-white"
                />
              </div>
            </div>

            {/* Return Date - deadline for quote submission */}
            <div className="space-y-2">
              <Label className="text-gray-300">Return Date</Label>
              <Input
                type="date"
                value={editData.return_date || ''}
                onChange={(e) => setEditData(prev => ({ ...prev, return_date: e.target.value }))}
                className="bg-[#1E2530] border-white/20 text-white"
                data-testid="edit-return-date"
              />
              <p className="text-xs text-gray-500">
                Deadline for submitting the quote to the client
              </p>
            </div>

            {/* Submitted Date - editable for manual override */}
            <div className="space-y-2">
              <Label className="text-gray-300">Submitted Date</Label>
              <Input
                type="date"
                value={editData.submitted_date || ''}
                onChange={(e) => setEditData(prev => ({ ...prev, submitted_date: e.target.value }))}
                className="bg-[#1E2530] border-white/20 text-white"
              />
              <p className="text-xs text-gray-500">
                Auto-filled when status changes to Submitted. Manually editable for corrections.
              </p>
            </div>

            {/* Quote Source */}
            <div className="space-y-2">
              <Label className="text-gray-300">Quote Source</Label>
              <Select 
                value={editData.quote_source || 'Manual'} 
                onValueChange={(v) => setEditData(prev => ({ ...prev, quote_source: v }))}
              >
                <SelectTrigger className="bg-[#1E2530] border-white/20 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#2A3240] border-white/10">
                  <SelectItem value="Manual" className="text-white focus:bg-[#F5A623]/20">Manual</SelectItem>
                  <SelectItem value="AI Generated" className="text-white focus:bg-[#F5A623]/20">AI Generated</SelectItem>
                  <SelectItem value="Hybrid" className="text-white focus:bg-[#F5A623]/20">Hybrid</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">Track whether AI assistance was used in creating this tender</p>
            </div>

            {/* Cost Breakdown Section */}
            <div className="space-y-3 p-3 bg-[#1E2530] rounded-lg">
              <Label className="text-[#F5A623] text-sm font-medium">Cost Breakdown (£)</Label>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-gray-300 text-xs">Labour</Label>
                  <Input
                    type="number"
                    value={editData.estimated_labour || ''}
                    onChange={(e) => setEditData(prev => ({ ...prev, estimated_labour: e.target.value }))}
                    placeholder="0"
                    data-testid="edit-labour"
                    className="bg-[#2A3240] border-white/20 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300 text-xs">Materials</Label>
                  <Input
                    type="number"
                    value={editData.estimated_materials || ''}
                    onChange={(e) => setEditData(prev => ({ ...prev, estimated_materials: e.target.value }))}
                    placeholder="0"
                    data-testid="edit-materials"
                    className="bg-[#2A3240] border-white/20 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300 text-xs">Plant</Label>
                  <Input
                    type="number"
                    value={editData.estimated_plant || ''}
                    onChange={(e) => setEditData(prev => ({ ...prev, estimated_plant: e.target.value }))}
                    placeholder="0"
                    data-testid="edit-plant"
                    className="bg-[#2A3240] border-white/20 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300 text-xs">Subcontractors</Label>
                  <Input
                    type="number"
                    value={editData.estimated_subcontractors || ''}
                    onChange={(e) => setEditData(prev => ({ ...prev, estimated_subcontractors: e.target.value }))}
                    placeholder="0"
                    data-testid="edit-subcontractors"
                    className="bg-[#2A3240] border-white/20 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300 text-xs">Prelims</Label>
                  <Input
                    type="number"
                    value={editData.estimated_prelims || ''}
                    onChange={(e) => setEditData(prev => ({ ...prev, estimated_prelims: e.target.value }))}
                    placeholder="0"
                    data-testid="edit-prelims"
                    className="bg-[#2A3240] border-white/20 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300 text-xs">Pro Sums</Label>
                  <Input
                    type="number"
                    value={editData.estimated_prosums || ''}
                    onChange={(e) => setEditData(prev => ({ ...prev, estimated_prosums: e.target.value }))}
                    placeholder="0"
                    data-testid="edit-prosums"
                    className="bg-[#2A3240] border-white/20 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300 text-xs">O&P (Overheads & Profit)</Label>
                  <Input
                    type="number"
                    value={editData.estimated_op || ''}
                    onChange={(e) => setEditData(prev => ({ ...prev, estimated_op: e.target.value }))}
                    placeholder="0"
                    data-testid="edit-op"
                    className="bg-[#2A3240] border-white/20 text-white"
                  />
                </div>
              </div>
              <div className="pt-2 border-t border-white/10">
                <Label className="text-gray-400 text-xs">Contract Value</Label>
                <p className="text-lg font-bold text-[#F5A623]">
                  {formatCurrency(
                    (parseFloat(editData.estimated_labour) || 0) + 
                    (parseFloat(editData.estimated_materials) || 0) + 
                    (parseFloat(editData.estimated_plant) || 0) + 
                    (parseFloat(editData.estimated_subcontractors) || 0) + 
                    (parseFloat(editData.estimated_prelims) || 0) + 
                    (parseFloat(editData.estimated_prosums) || 0) + 
                    (parseFloat(editData.estimated_op) || 0)
                  )}
                </p>
              </div>
            </div>

            {/* Estimator Assignment */}
            <div className="space-y-2">
              <Label className="text-gray-300 flex items-center gap-2">
                <UserCog className="w-4 h-4" />
                Estimator
              </Label>
              <Select 
                value={editData.estimator_id || 'unassigned'} 
                onValueChange={(v) => setEditData(prev => ({ ...prev, estimator_id: v === 'unassigned' ? '' : v }))}
              >
                <SelectTrigger className="bg-[#1E2530] border-white/20 text-white">
                  <SelectValue placeholder="Select estimator" />
                </SelectTrigger>
                <SelectContent className="bg-[#2A3240] border-white/10">
                  <SelectItem value="unassigned" className="text-gray-400 focus:bg-[#F5A623]/20">
                    Not assigned
                  </SelectItem>
                  {adminUsers.map(admin => (
                    <SelectItem 
                      key={admin.id} 
                      value={admin.id} 
                      className="text-white focus:bg-[#F5A623]/20"
                    >
                      {admin.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Structural Engineer Assignment */}
            <div className="space-y-2">
              <Label className="text-gray-300 flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                Structural Engineer
              </Label>
              <Select 
                value={editData.structural_engineer_id || 'unassigned'} 
                onValueChange={(v) => setEditData(prev => ({ ...prev, structural_engineer_id: v === 'unassigned' ? '' : v }))}
              >
                <SelectTrigger className="bg-[#1E2530] border-white/20 text-white" data-testid="structural-engineer-select">
                  <SelectValue placeholder="Select structural engineer" />
                </SelectTrigger>
                <SelectContent className="bg-[#2A3240] border-white/10">
                  <SelectItem value="unassigned" className="text-gray-400 focus:bg-[#F5A623]/20">
                    Not assigned
                  </SelectItem>
                  {structuralEngineers.map(engineer => (
                    <SelectItem 
                      key={engineer.id} 
                      value={engineer.id} 
                      className="text-white focus:bg-[#F5A623]/20"
                    >
                      {engineer.company || engineer.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {structuralEngineers.length === 0 && (
                <p className="text-xs text-gray-500">No structural engineers found. Add one in Contacts.</p>
              )}
            </div>

            {/* Architect Assignment */}
            <div className="space-y-2">
              <Label className="text-gray-300 flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                Architect
              </Label>
              <Select 
                value={editData.architect_id || 'unassigned'} 
                onValueChange={(v) => setEditData(prev => ({ ...prev, architect_id: v === 'unassigned' ? '' : v }))}
              >
                <SelectTrigger className="bg-[#1E2530] border-white/20 text-white" data-testid="architect-select">
                  <SelectValue placeholder="Select architect" />
                </SelectTrigger>
                <SelectContent className="bg-[#2A3240] border-white/10">
                  <SelectItem value="unassigned" className="text-gray-400 focus:bg-[#F5A623]/20">
                    Not assigned
                  </SelectItem>
                  {architects.map(architect => (
                    <SelectItem 
                      key={architect.id} 
                      value={architect.id} 
                      className="text-white focus:bg-[#F5A623]/20"
                    >
                      {architect.company || architect.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {architects.length === 0 && (
                <p className="text-xs text-gray-500">No architects found. Add one in Contacts.</p>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">Notes</Label>
              <Textarea
                value={editData.notes}
                onChange={(e) => setEditData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Internal notes..."
                rows={3}
                className="bg-[#1E2530] border-white/20 text-white resize-none"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button 
                variant="outline" 
                onClick={() => setEditDialogOpen(false)} 
                className="flex-1 border-white/20 text-gray-300"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleUpdate} 
                disabled={submitting}
                data-testid="save-tender-btn"
                className="flex-1 bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D]"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Enquiry Dialog */}
      <Dialog open={enquiryDialogOpen} onOpenChange={setEnquiryDialogOpen}>
        <DialogContent className="bg-[#2A3240] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Send className="w-5 h-5 text-[#F5A623]" />
              Add Supplier Enquiry
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Select a supplier from contacts or enter details manually.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Select Supplier</Label>
              <Select 
                value={newEnquiry.supplier_id || 'manual'} 
                onValueChange={handleSupplierSelect}
              >
                <SelectTrigger className="bg-[#1E2530] border-white/20 text-white" data-testid="enquiry-supplier-select">
                  <SelectValue placeholder="Choose from contacts or enter manually" />
                </SelectTrigger>
                <SelectContent className="bg-[#2A3240] border-white/10 max-h-60">
                  <SelectItem value="manual" className="text-gray-400 focus:bg-[#F5A623]/20">
                    Enter manually
                  </SelectItem>
                  {suppliers.map(supplier => (
                    <SelectItem 
                      key={supplier.id} 
                      value={supplier.id} 
                      className="text-white focus:bg-[#F5A623]/20"
                    >
                      {supplier.company || supplier.name} {supplier.trade ? `(${supplier.trade})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {suppliers.length === 0 && (
                <p className="text-xs text-gray-500">No suppliers found. Add suppliers in Contacts page.</p>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Supplier Name *</Label>
              <Input
                value={newEnquiry.supplier_name}
                onChange={(e) => setNewEnquiry(prev => ({ ...prev, supplier_name: e.target.value }))}
                placeholder="Enter supplier name..."
                data-testid="enquiry-supplier-input"
                className="bg-[#1E2530] border-white/20 text-white"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-gray-300">Email</Label>
                <Input
                  value={newEnquiry.supplier_email}
                  onChange={(e) => setNewEnquiry(prev => ({ ...prev, supplier_email: e.target.value }))}
                  placeholder="supplier@email.com"
                  type="email"
                  className="bg-[#1E2530] border-white/20 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Trade</Label>
                <Input
                  value={newEnquiry.trade}
                  onChange={(e) => setNewEnquiry(prev => ({ ...prev, trade: e.target.value }))}
                  placeholder="e.g. Bricklayer"
                  className="bg-[#1E2530] border-white/20 text-white"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Description *</Label>
              <Textarea
                value={newEnquiry.description}
                onChange={(e) => setNewEnquiry(prev => ({ ...prev, description: e.target.value }))}
                placeholder="What was enquired about..."
                rows={3}
                data-testid="enquiry-description-input"
                className="bg-[#1E2530] border-white/20 text-white resize-none"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-gray-300">Date Sent</Label>
                <Input
                  type="date"
                  value={newEnquiry.date_sent}
                  onChange={(e) => setNewEnquiry(prev => ({ ...prev, date_sent: e.target.value }))}
                  className="bg-[#1E2530] border-white/20 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Status</Label>
                <Select 
                  value={newEnquiry.status} 
                  onValueChange={(v) => setNewEnquiry(prev => ({ ...prev, status: v }))}
                >
                  <SelectTrigger className="bg-[#1E2530] border-white/20 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#2A3240] border-white/10">
                    {ENQUIRY_STATUS_OPTIONS.map(status => (
                      <SelectItem key={status} value={status} className="text-white focus:bg-[#F5A623]/20">
                        {status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button 
                variant="outline" 
                onClick={() => { 
                  setEnquiryDialogOpen(false); 
                  setNewEnquiry({
                    supplier_id: '',
                    supplier_name: '',
                    supplier_email: '',
                    trade: '',
                    description: '',
                    date_sent: format(new Date(), 'yyyy-MM-dd'),
                    status: 'Pending'
                  }); 
                }} 
                className="flex-1 border-white/20 text-gray-300"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleAddEnquiry} 
                disabled={submitting || !newEnquiry.supplier_name.trim() || !newEnquiry.description.trim()}
                data-testid="save-enquiry-btn"
                className="flex-1 bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D]"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Enquiry'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Enquiry Dialog */}
      <Dialog open={editEnquiryDialogOpen} onOpenChange={setEditEnquiryDialogOpen}>
        <DialogContent className="bg-[#2A3240] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Edit className="w-5 h-5 text-[#F5A623]" />
              Edit Supplier Enquiry
            </DialogTitle>
          </DialogHeader>
          {editingEnquiry && (
            <div className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Supplier Name</Label>
                <Input
                  value={editingEnquiry.supplier_name}
                  onChange={(e) => setEditingEnquiry(prev => ({ ...prev, supplier_name: e.target.value }))}
                  className="bg-[#1E2530] border-white/20 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Description</Label>
                <Textarea
                  value={editingEnquiry.description}
                  onChange={(e) => setEditingEnquiry(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="bg-[#1E2530] border-white/20 text-white resize-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-gray-300">Date Sent</Label>
                  <Input
                    type="date"
                    value={editingEnquiry.date_sent}
                    onChange={(e) => setEditingEnquiry(prev => ({ ...prev, date_sent: e.target.value }))}
                    className="bg-[#1E2530] border-white/20 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">Status</Label>
                  <Select 
                    value={editingEnquiry.status} 
                    onValueChange={(v) => setEditingEnquiry(prev => ({ ...prev, status: v }))}
                  >
                    <SelectTrigger className="bg-[#1E2530] border-white/20 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#2A3240] border-white/10">
                      {ENQUIRY_STATUS_OPTIONS.map(status => (
                        <SelectItem key={status} value={status} className="text-white focus:bg-[#F5A623]/20">
                          {status}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Response Notes</Label>
                <Textarea
                  value={editingEnquiry.response_notes || ''}
                  onChange={(e) => setEditingEnquiry(prev => ({ ...prev, response_notes: e.target.value }))}
                  placeholder="Notes about the supplier's response..."
                  rows={3}
                  className="bg-[#1E2530] border-white/20 text-white resize-none"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <Button 
                  variant="outline" 
                  onClick={() => { setEditEnquiryDialogOpen(false); setEditingEnquiry(null); }} 
                  className="flex-1 border-white/20 text-gray-300"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleUpdateEnquiry} 
                  disabled={submitting}
                  className="flex-1 bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D]"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Email Supplier Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="bg-[#2A3240] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-400" />
              Email Supplier
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Select a template and open in Outlook to send.
            </DialogDescription>
          </DialogHeader>
          {selectedEmailEnquiry && (
            <div className="space-y-4 pt-4">
              <div className="p-3 bg-[#1E2530] rounded-lg border border-white/10">
                <p className="text-white font-medium">{selectedEmailEnquiry.supplier_name}</p>
                <p className="text-sm text-gray-400">{selectedEmailEnquiry.supplier_email}</p>
                {selectedEmailEnquiry.trade && (
                  <p className="text-xs text-blue-400 mt-1">{selectedEmailEnquiry.trade}</p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label className="text-gray-300">Email Template *</Label>
                <Select 
                  value={selectedTemplate} 
                  onValueChange={setSelectedTemplate}
                >
                  <SelectTrigger className="bg-[#1E2530] border-white/20 text-white" data-testid="email-template-select">
                    <SelectValue placeholder="Select template" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#2A3240] border-white/10">
                    {emailTemplates.map(template => (
                      <SelectItem 
                        key={template.id} 
                        value={template.id} 
                        className="text-white focus:bg-[#F5A623]/20"
                      >
                        {template.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {emailTemplates.length === 0 && (
                  <p className="text-xs text-gray-500">No email templates found. Go to Settings to create templates.</p>
                )}
              </div>

              {/* Template Preview */}
              {selectedTemplate && (
                <div className="space-y-2">
                  <Label className="text-gray-300 text-xs">Preview</Label>
                  <div className="p-3 bg-[#1E2530] rounded-lg border border-white/10 text-xs text-gray-400 max-h-40 overflow-y-auto">
                    {(() => {
                      const template = emailTemplates.find(t => t.id === selectedTemplate);
                      if (!template) return null;
                      const projectTypes = Array.isArray(tender.project_types) ? tender.project_types.join(', ') : tender.project_types || '';
                      const previewBody = template.body
                        .replace('{supplier_name}', selectedEmailEnquiry.supplier_name)
                        .replace('{tender_ref}', tender.tender_ref)
                        .replace('{address}', tender.site_address || tender.address)
                        .replace('{postcode}', tender.site_postcode || tender.postcode)
                        .replace('{project_types}', projectTypes)
                        .replace('{trade}', selectedEmailEnquiry.trade || 'General')
                        .replace('{description}', selectedEmailEnquiry.description)
                        .replace('{user_name}', user?.name || 'LUX Team');
                      return <pre className="whitespace-pre-wrap font-sans">{previewBody}</pre>;
                    })()}
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <Button 
                  variant="outline" 
                  onClick={() => { setEmailDialogOpen(false); setSelectedEmailEnquiry(null); }} 
                  className="flex-1 border-white/20 text-gray-300"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={() => {
                    const template = emailTemplates.find(t => t.id === selectedTemplate);
                    if (template && selectedEmailEnquiry) {
                      const mailtoLink = generateMailtoLink(selectedEmailEnquiry, template);
                      if (mailtoLink) {
                        handleEmailClick(selectedEmailEnquiry, template);
                        window.open(mailtoLink, '_blank');
                        setEmailDialogOpen(false);
                        setSelectedEmailEnquiry(null);
                      }
                    }
                  }}
                  disabled={!selectedTemplate}
                  data-testid="send-email-btn"
                  className="flex-1 bg-blue-500 text-white hover:bg-blue-600"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Open in Outlook
                </Button>
              </div>
              
              <p className="text-xs text-gray-500 text-center">
                This will open your email client with the template pre-filled. Remember to attach drawings manually.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Feedback Survey Dialog - Item 4 */}
      <Dialog open={feedbackSurveyDialogOpen} onOpenChange={setFeedbackSurveyDialogOpen}>
        <DialogContent className="bg-[#2A3240] border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-[#F5A623]" />
              Send Feedback Survey?
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-300">
              Would you like to send a feedback survey to{' '}
              <span className="text-white font-medium">{tender?.client_name}</span> at{' '}
              <span className="text-[#F5A623]">{tender?.email}</span>?
            </p>
            <div className="mt-4 p-3 bg-[#1E2530] rounded-lg border border-white/10">
              <p className="text-xs text-gray-500 uppercase mb-2">Email Preview</p>
              <p className="text-sm text-gray-400 italic">
                "Thank you for giving us the opportunity to quote for your project. We would love 60 seconds of your time — just 5 quick questions..."
              </p>
            </div>
          </div>
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleFeedbackSurveySkip}
              className="border-white/20 text-gray-300 hover:bg-white/10"
              data-testid="feedback-skip-btn"
            >
              Skip
            </Button>
            <Button
              onClick={handleFeedbackSurveyConfirm}
              className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D]"
              data-testid="feedback-confirm-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              Send Survey
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contract & H&S Dialog - Item 5 */}
      <Dialog open={contractDialogOpen} onOpenChange={setContractDialogOpen}>
        <DialogContent className="bg-[#2A3240] border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-green-500" />
              Generate Contract Documents?
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-300">
              Would you like to generate the contract and H&S documents for this project?
            </p>
            <div className="mt-4 p-3 bg-green-500/10 rounded-lg border border-green-500/20">
              <p className="text-sm text-green-400">
                This will queue the contract and health & safety documents for generation. The actual document creation will be handled in a future build phase.
              </p>
            </div>
          </div>
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleContractSkip}
              className="border-white/20 text-gray-300 hover:bg-white/10"
              data-testid="contract-skip-btn"
            >
              Skip
            </Button>
            <Button
              onClick={handleContractConfirm}
              className="bg-green-500 text-white hover:bg-green-600"
              data-testid="contract-confirm-btn"
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Generate Documents
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generate Tender Dialog - Item 6 */}
      <Dialog open={generateTenderDialogOpen} onOpenChange={setGenerateTenderDialogOpen}>
        <DialogContent className="bg-[#2A3240] border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-cyan-400" />
              Generate Tender
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-300">
              This will trigger Claude to read the project drawings and checklist from the AI Inputs folder and generate a draft tender.
            </p>
            <div className="mt-4 p-3 bg-cyan-500/10 rounded-lg border border-cyan-500/20">
              <p className="text-sm text-cyan-400">
                <strong>Note:</strong> The AI integration will be connected in a later build phase. For now, this sets the generation status and logs the request.
              </p>
            </div>
          </div>
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setGenerateTenderDialogOpen(false)}
              className="border-white/20 text-gray-300 hover:bg-white/10"
            >
              Cancel
            </Button>
            <Button
              onClick={handleGenerateTenderConfirm}
              disabled={submitting}
              className="bg-cyan-500 text-white hover:bg-cyan-600"
              data-testid="generate-confirm-btn"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <ClipboardList className="w-4 h-4 mr-2" />
              )}
              Confirm Generation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <CaptureFAB projectId={null} company={tender?.tender_type === 'CON' ? 'CON' : 'DEV'} />
    </div>
  );
}
